/**
 * How to render each per-vertical `details` key.
 *
 * Which keys a vertical accepts comes from the service at runtime
 * (`detailKeysByVertical` on `GET /leads/config`) — this only says what widget
 * a key needs and what to call it. Keeping the key list on the server is the
 * point: the service **rejects** an unknown key rather than dropping it, so a
 * local copy that drifts produces a 400 the operator cannot act on.
 *
 * A key absent from here still renders, as text. That is deliberate: a new
 * detail key should appear in the form the day the service accepts it, not the
 * day someone remembers to edit this file.
 */
export type DetailFieldKind = 'text' | 'number' | 'date' | 'list' | 'lookup';

/**
 * Where a `lookup` field's options come from.
 *
 * Every one is a list this admin already fetches for other screens, so the
 * form offers what actually exists rather than asking someone to type a unit
 * type group from memory and hoping it matches.
 */
export type LookupSource = 'areaNames' | 'unitTypeGroups' | 'typeOfUnits';

export interface DetailFieldConfig {
  label: string;
  kind: DetailFieldKind;
  /** Required when `kind` is `lookup`. */
  source?: LookupSource;
  hint?: string;
}

export const DETAIL_FIELDS: Record<string, DetailFieldConfig> = {
  // furnishing
  unitTypeGroup: { label: 'Unit type group', kind: 'lookup', source: 'unitTypeGroups' },
  typeOfUnit: { label: 'Type of unit', kind: 'lookup', source: 'typeOfUnits' },
  packageId: {
    label: 'Package ID',
    kind: 'number',
    hint: 'Catalogue id in the furnishing service'
  },
  modelId: { label: 'Model ID', kind: 'number' },
  addons: { label: 'Add-ons', kind: 'list', hint: 'Type each one and press enter' },
  budget: { label: 'Budget', kind: 'number' },
  furnishingRequestId: {
    label: 'Furnishing request ID',
    kind: 'number',
    hint: 'Set by the system when the lead is won — rarely entered by hand'
  },

  // acquisition
  propertyType: { label: 'Property type', kind: 'lookup', source: 'typeOfUnits' },
  bedrooms: { label: 'Bedrooms', kind: 'number' },
  unitCount: {
    label: 'Units offered',
    kind: 'number',
    hint: 'A portfolio owner is a different lead'
  },
  // No vocabulary for this exists in any service — genuinely free text.
  furnishingStatus: { label: 'Furnishing status', kind: 'text' },
  expectedMonthlyRent: {
    label: 'Expected monthly rent',
    kind: 'number',
    hint: 'The most common reason acquisition leads fail'
  },
  availableFrom: { label: 'Available from', kind: 'date' },
  contactTime: { label: 'Best time to call', kind: 'text' },
  propertyId: {
    label: 'Property ID',
    kind: 'text',
    hint: 'Set by the system when the lead is won'
  },

  // reservation
  checkIn: { label: 'Check in', kind: 'date' },
  checkOut: { label: 'Check out', kind: 'date' },
  nights: { label: 'Nights', kind: 'number' },
  guests: { label: 'Guests', kind: 'number' },
  unitTypeId: { label: 'Unit type ID', kind: 'text' },
  budgetPerNight: { label: 'Budget per night', kind: 'number' },
  reservationId: {
    label: 'Reservation ID',
    kind: 'number',
    hint: 'Set by the system when the lead is won'
  },

  // shared across verticals
  areaName: { label: 'Area', kind: 'lookup', source: 'areaNames' }
};

export function detailFieldFor(key: string): DetailFieldConfig {
  return DETAIL_FIELDS[key] ?? { label: key, kind: 'text' };
}
